import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { useDataContext } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/context/UtilizePastDetailsDataContext';
import { useTranslation } from '@/i18n';
import {
  IconInfo,
  RelationDialogBody,
} from '@moneyforward/ap-frontend-components';
import { ApPayeeDetail as ApPayeeModel } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { EmptyPreview, MainPreview } from '../Preview';
import styles from './Body.module.scss';
import { PaymentRequestRow, usePaymentRequestTable } from './hooks';

const cx = classnames.bind(styles);

type Props = {
  selectedPayee?: ApPayeeModel;
};

const InnerDialogBody: FC<Props> = memo(({ selectedPayee }) => {
  const { t } = useTranslation();
  const payeeId = selectedPayee?.id;
  const { onSetSelectedFile, notificationDrawer, selectedFile } =
    useDataContext();
  const { tableData, isLoading, notification, columns } =
    usePaymentRequestTable(payeeId);

  const pagination = useMemo(() => ({ maxPage: 1 }), []);

  const pdfViewer = selectedFile?.fileUrl ? (
    <MainPreview url={selectedFile.fileUrl} />
  ) : (
    <EmptyPreview />
  );

  const tableTitle = useMemo(
    () => (
      <div className={cx(styles['tableTitle'])}>
        <IconInfo size={14} />
        <span>{t('payment_past_details_table_title')}</span>
      </div>
    ),
    [t]
  );

  const getVisibleNotification = useMemo(() => {
    if (notification && notification.visible) {
      return notification;
    }
    if (notificationDrawer && notificationDrawer.visible) {
      return notificationDrawer;
    }
    return undefined;
  }, [notification, notificationDrawer]);
  return (
    <>
      <RelationDialogBody<PaymentRequestRow>
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        notification={getVisibleNotification}
        rowKey='id'
        tableTitle={tableTitle}
        pdfViewer={pdfViewer}
        pagination={pagination}
        rowMode='radio'
        contentContainerClassName={cx(styles['contentContainer'])}
        tableScrollY={476}
        onSelectedRows={({ fileUrl, id }: PaymentRequestRow) => {
          onSetSelectedFile({
            invoiceId: id,
            fileUrl: fileUrl ?? '',
          });
        }}
        selectedItems={
          selectedFile && 'invoiceId' in selectedFile
            ? [selectedFile.invoiceId]
            : undefined
        }
        emptyType='search'
        emptyText={t('payment_past_details_empty_message')}
      />
    </>
  );
});
InnerDialogBody.displayName = 'InnerUtilizePastDetailsDialogBody';

export const DialogBody: FC<Props> = memo((props) => {
  return (
    <SuspenseErrorBoundary>
      <InnerDialogBody {...props} />
    </SuspenseErrorBoundary>
  );
});
DialogBody.displayName = 'UtilizePastDetailsDialogBody';
